import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import ThemeToggle from './ThemeToggle';

interface HeaderProps {
  className?: string;
}

interface NavigationItem {
  label: string;
  path: string;
  children?: NavigationItem[];
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems: NavigationItem[] = [
    { label: 'Home', path: '/' }, // Changed from /homepage to / for root
    {
      label: 'Services',
      path: '/services-overview',
      children: [
        { label: 'Services Overview', path: '/services-overview' },
        { label: 'AI Chatbot Automation', path: '/ai-chatbot-automation' },
        { label: 'n8n & Make.com Workflows', path: '/n8n-make-com-workflows' },
        { label: 'WhatsApp & Email', path: '/whats-app-email-automation' },
        { label: 'Custom Integrations', path: '/custom-api-integrations' },
        { label: 'SEO Services', path: '/seo-services' },
        { label: 'Local SEO', path: '/local-seo' },
        { label: 'Social Media Marketing', path: '/social-media-marketing' },
        { label: 'Google Ads & PPC', path: '/google-ads-ppc' },
        { label: 'Meta Ads', path: '/meta-ads' },
        { label: 'Content Marketing', path: '/content-marketing' },
        { label: 'Email Marketing', path: '/email-marketing' },
        { label: 'Analytics & CRO', path: '/analytics-and-cro' },
      ],
    },
    { label: 'Industry Solutions', path: '/industry-solutions' },
    { label: 'Blog', path: '/blog' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Case Studies', path: '/case-studies' },
    { label: 'About', path: '/about-us' }, // Shortened for better spacing
    { label: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
  };

  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const hasActiveChild = (children?: NavigationItem[]) => {
    return children?.some(child => isActivePath(child.path));
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/80 backdrop-blur-md border-border shadow-sm' 
          : 'bg-background border-border'
      } ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          
          {/* Logo Area */}
          
          <Link
            to="/"
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200"
            aria-label="Make Mind Automations Home"
          >
              <div className="flex items-center justify-center w-9 h-9 bg-primary rounded-lg text-white shadow-sm">
              <Icon name="Zap" className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={3} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-foreground leading-none tracking-tight">
                Make Mind
              </span>
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider leading-none mt-0.5">
                Automations
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" aria-label="Main navigation">
            {navigationItems.map((item) => (
              <div key={item.path} className="relative group">
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.label)}
                      onMouseEnter={() => setActiveDropdown(item.label)}
                      className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        hasActiveChild(item.children) || activeDropdown === item.label
                          ? 'text-primary bg-primary/5' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      aria-expanded={activeDropdown === item.label}
                    >
                      <span>{item.label}</span>
                      <Icon
                        name="ChevronDown"
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${
                          activeDropdown === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === item.label && (
                      <div 
                        className="absolute top-full left-0 mt-2 w-64 bg-popover rounded-xl shadow-xl border border-border py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`block px-4 py-2.5 text-sm transition-colors duration-200 ${
                              isActivePath(child.path)
                                ? 'text-primary bg-primary/5 font-medium' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActivePath(item.path)
                        ? 'text-primary bg-primary/5' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            <ThemeToggle />
            <Link to="/contact">
              <Button variant="default" size="sm" iconName="Calendar" iconPosition="left" className="shadow-md shadow-primary/20">
                Book Audit
              </Button>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center space-x-3 lg:hidden">
            <ThemeToggle />
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl h-screen fixed w-full overflow-y-auto pb-24">
          <nav className="px-4 py-6 space-y-2" aria-label="Mobile navigation">
            {navigationItems.map((item) => (
              <div key={item.path} className="space-y-2">
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.label)}
                      className={`flex items-center justify-between w-full px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                        hasActiveChild(item.children)
                          ? 'text-primary bg-primary/5' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <span>{item.label}</span>
                      <Icon
                        name="ChevronDown"
                        className={`w-4 h-4 transition-transform duration-200 ${
                          activeDropdown === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    {activeDropdown === item.label && (
                      <div className="ml-4 pl-4 border-l border-border space-y-1 animate-in slide-in-from-left-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`block px-4 py-3 text-sm rounded-lg transition-colors ${
                              isActivePath(child.path)
                                ? 'text-primary font-medium' 
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                      isActivePath(item.path)
                        ? 'text-primary bg-primary/5' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            
            <div className="pt-6 mt-6 border-t border-border space-y-4">
              <Link to="/contact" className="block">
                <Button variant="default" size="lg" fullWidth iconName="Calendar">
                  Book Free Audit
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
